import { ChangeEvent, FormEvent, useMemo, useState } from 'react';

type FormStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

type FormValues = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  companyAlt: string;
};

const SUBJECT_OPTIONS = [
  { value: '', label: '選択してください' },
  { value: 'software', label: 'ソフトウェア開発' },
  { value: 'consulting', label: 'ITコンサルティング' },
  { value: 'web', label: 'Web・デジタルコンテンツ' },
  { value: 'other', label: 'その他' },
];

const INITIAL_FORM_VALUES: FormValues = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  companyAlt: '',
};

export default function ContactSection() {
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_VALUES);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');
  const [agreed, setAgreed] = useState(false);

  const selectedSubjectLabel = useMemo(() => {
    return SUBJECT_OPTIONS.find((option) => option.value === formValues.subject)?.label || '未選択';
  }, [formValues.subject]);

  const resetForm = () => {
    setFormValues(INITIAL_FORM_VALUES);
    setAgreed(false);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formValues.companyAlt.trim() !== '') {
      setFormStatus('success');
      return;
    }

    if (!agreed) {
      setFormError('プライバシーポリシーに同意してください。');
      setFormStatus('error');
      return;
    }

    setFormError('');
    setFormStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams({
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone,
        subject: selectedSubjectLabel === '選択してください' ? '' : selectedSubjectLabel,
        message: formValues.message,
        privacy: agreed ? '同意済み' : '',
      });

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: params.toString(),
      });

      const responseText = (await response.text()).trim();

      if (response.ok && responseText === 'OK') {
        setFormStatus('success');
        resetForm();
      } else {
        setFormError('送信に失敗しました。お急ぎの場合はお電話またはメールにてお問い合わせください。');
        setFormStatus('error');
      }
    } catch {
      setFormError('送信に失敗しました。お急ぎの場合はお電話またはメールにてお問い合わせください。');
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-background-100">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="w-full lg:w-[38%]">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-[0.2em] mb-5">
              CONTACT
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground-900 tracking-wide mb-6">
              Contact Us
            </h2>
            <p className="text-foreground-500 text-sm leading-relaxed mb-10">
              お仕事のご依頼やご相談など、お気軽にお問い合わせください。
              通常2営業日以内にご返信いたします。
            </p>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-200">
                  <i className="ri-phone-line text-foreground-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-xs mb-0.5">電話</p>
                  <a href="tel:050-8896-3509" className="text-foreground-800 text-sm hover:text-primary-600 transition-colors">
                    050-8896-3509
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-200">
                  <i className="ri-mail-line text-foreground-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-xs mb-0.5">メール</p>
                  <a href="mailto:admin@rse-tko.com" className="text-foreground-800 text-sm hover:text-primary-600 transition-colors">
                    admin@rse-tko.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-200">
                  <i className="ri-map-pin-line text-foreground-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-xs mb-0.5">所在地</p>
                  <p className="text-foreground-800 text-sm">
                    〒161-0033 東京都新宿区下落合一丁目15番20-801号
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[62%]">
            <form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/80"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="contact-name" className="block text-foreground-600 text-sm mb-2">
                    お名前 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-foreground-600 text-sm mb-2">
                    メールアドレス <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="example@domain.com"
                  />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="contact-phone" className="block text-foreground-600 text-sm mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="090-1234-5678"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="contact-subject" className="block text-foreground-600 text-sm mb-2">
                  お問い合わせ種別
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formValues.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors appearance-none"
                >
                  {SUBJECT_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="contact-message" className="block text-foreground-600 text-sm mb-2">
                  お問い合わせ内容 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formValues.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-foreground-400 text-xs mt-1 text-right">最大500文字</p>
              </div>

              <input
                type="text"
                name="companyAlt"
                value={formValues.companyAlt}
                onChange={handleInputChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="sr-only"
              />

              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                  />
                  <span className="text-foreground-500 text-sm leading-relaxed">
                    <a
                      href="/privacy-policy"
                      className="text-primary-600 underline hover:text-primary-700 transition-colors"
                    >
                      プライバシーポリシー
                    </a>
                    に同意します
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <>送信中... <i className="ri-loader-4-line animate-spin ml-2"></i></>
                ) : (
                  <>送信する <i className="ri-send-plane-line ml-2"></i></>
                )}
              </button>

            </form>
          </div>
        </div>
      </div>

      {formStatus === 'confirm' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 md:px-8 py-6 border-b border-background-200">
              <p className="text-xs tracking-[0.2em] text-primary-600 mb-2">CONFIRM</p>
              <h3 className="font-heading text-2xl md:text-3xl text-foreground-900">入力内容の確認</h3>
              <p className="text-sm text-foreground-500 mt-2">送信前に内容をご確認ください。</p>
            </div>

            <div className="px-6 md:px-8 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">お名前</p>
                  <p className="text-sm text-foreground-800 break-words">{formValues.name}</p>
                </div>
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">メールアドレス</p>
                  <p className="text-sm text-foreground-800 break-all">{formValues.email}</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">電話番号</p>
                  <p className="text-sm text-foreground-800 break-words">{formValues.phone || '未入力'}</p>
                </div>
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">お問い合わせ種別</p>
                  <p className="text-sm text-foreground-800 break-words">
                    {selectedSubjectLabel === '選択してください' ? '未選択' : selectedSubjectLabel}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-background-100 px-4 py-3">
                <p className="text-xs text-foreground-400 mb-1">お問い合わせ内容</p>
                <p className="text-sm text-foreground-800 whitespace-pre-wrap break-words">{formValues.message}</p>
              </div>
            </div>

            <div className="px-6 md:px-8 py-5 border-t border-background-200 flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setFormStatus('idle')}
                className="px-5 py-3 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="px-5 py-3 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
              >
                送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {formStatus === 'submitting' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-5">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
            </div>
            <h3 className="font-heading text-2xl text-foreground-900 mb-2">送信中</h3>
            <p className="text-sm text-foreground-500">お問い合わせ内容を送信しています。</p>
          </div>
        </div>
      )}

      {formStatus === 'success' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-accent-50 flex items-center justify-center mb-5">
              <i className="ri-checkbox-circle-line text-2xl text-accent-700"></i>
            </div>
            <h3 className="font-heading text-2xl text-foreground-900 mb-2">送信完了</h3>
            <p className="text-sm text-foreground-500 mb-6">
              お問い合わせを受け付けました。通常2営業日以内にご返信いたします。
            </p>
            <button
              type="button"
              onClick={() => setFormStatus('idle')}
              className="px-6 py-3 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {formStatus === 'error' && formError && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-5">
              <i className="ri-error-warning-line text-2xl text-red-600"></i>
            </div>
            <h3 className="font-heading text-2xl text-foreground-900 mb-2">送信失敗</h3>
            <p className="text-sm text-foreground-500 mb-6">
              {formError}
            </p>
            <button
              type="button"
              onClick={() => setFormStatus('idle')}
              className="px-6 py-3 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
